import React, { useState, useEffect } from "react";
import { Button, Checkbox } from "antd";
import { fetchSpecialityTags, saveUserPreferences } from "../service";
import backIcon from "../../../assets/images/arrow-left.svg";
import personalizeIcon from "../../../assets/images/personalize.svg";
import "./TatvaShots.css";
import { useNavigate, useLocation } from "react-router-dom";
import PersonalizeDrawer from "./PersonalizeDrawer";
import useStore from "../../../store/useStore";
import useUserPreferences from "../../../hooks/usePreferences";
import useCategories from "../../../hooks/useCategories";
import { getDecodedToken } from "../../../utils/localStorage";
import { trackZymedEvent, ZYMED_EVENTS } from "../../../utils/zymedAnalytics";

const PreferenceSettings: React.FC = () => {
  useEffect(() => {
    trackZymedEvent(ZYMED_EVENTS.SHOTS_PREFERENCES);
  }, []);
  const [preferences, setPreferences] = useState<number[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const returnTab = (location.state as any)?.returnTab;
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState<any>({});
  const [currentCategory, setCurrentCategory] = useState<any>({});
  const { tatvaShots } = useStore();
  const { getUserPreferences } = useUserPreferences();
  const { getCategories } = useCategories();
  const token: any = getDecodedToken();
  const { user } = token;
  const { speciality, specialityTxt } = user;
  const [tagsLoader, setTagsLoader] = useState<boolean>(false);

  // Fetch categories from API
  useEffect(() => {
    (async () => {
      let prefs = tatvaShots?.userPreferences;
      if (!prefs?.length) prefs = await getUserPreferences();
      if (!tatvaShots?.categories?.length) await getCategories();
      setPreferences(
        prefs || [{ speciality_id: speciality, code: specialityTxt }]
      );
      const tags = prefs?.reduce((acc: any, cur: any) => {
        if (acc?.[cur?.code]) {
          acc[cur?.code] = [...acc?.[cur?.code], cur?.tag_id];
          return acc;
        }
        acc[cur?.code] = [cur?.tag_id];
        return acc;
      }, {});
      setSelectedTags(tags || {});
    })();
  }, []);

  // Handle checkbox toggle
  const handleCheckboxChange = (category: any) => {
    setPreferences((prevPreferences) =>
      !!prevPreferences?.find(
        (pref: any) => pref?.speciality_id === category?.id
      )
        ? prevPreferences.filter(
          (pref: any) => pref?.speciality_id !== category?.id
        )
        : [...prevPreferences, { ...category, speciality_id: category?.id }]
    );
  };

  const handleSelectTag = (tag: number) => {
    setSelectedTags((prevTags: any) => {
      if (prevTags?.[currentCategory?.code]) {
        if (prevTags?.[currentCategory?.code].includes(tag)) {
          prevTags[currentCategory?.code] = prevTags?.[
            currentCategory?.code
          ].filter((id: any) => id !== tag);
        } else {
          prevTags[currentCategory?.code] = [
            ...prevTags[currentCategory?.code],
            tag,
          ];
        }
      } else {
        prevTags[currentCategory?.code] = [tag];
      }
      return { ...prevTags };
    });
  };

  const handleSavePreferences = async () => {
    try {
      setLoader(true);
      const selected = tatvaShots?.categories.filter(
        (category: any) =>
          !!preferences?.find(
            (pref: any) => pref?.speciality_id === category?.id
          )
      );

      // Optimize by not fetching tags if none are selected for that category
      const specialityTags = selected.map((category) => {
        const tags = selectedTags?.[category.code] || [];
        return { id: category.id, tags };
      });

      const payload = { prefs: specialityTags };

      const res = await saveUserPreferences(payload);
      if (res?.status === 200) {
        trackZymedEvent(ZYMED_EVENTS.SHOTS_SAVE_PREFERENCES, {
          selected_preferences: selected.map(
            (category: any) => category?.name ?? category?.code
          ),
        });
        await getUserPreferences();
        // Navigate back to the correct tab if returnTab is provided, otherwise use browser history
        if (returnTab) {
          navigate("/short", { state: { type: returnTab } });
        } else {
          navigate(-1);
        }
      }
    } catch (error) {
      console.error("Error in getting started:", error);
    } finally {
      setLoader(false);
    }
  };

  const getSpecialityTags = async (specialityId: number) => {
    setTagsLoader(true);
    const res: any = await fetchSpecialityTags(specialityId);
    setTagsLoader(false);
    setTags(res);
    return res;
  };

  const handlePersonalize = (speciality: any) => {
    setCurrentCategory(speciality);
    if (speciality?.id) getSpecialityTags(speciality?.id);
    handlePersonalizeDrawer();
  };

  const handlePersonalizeDrawer = () => {
    setShowDrawer(!showDrawer);
  };

  const isAllSelected = () => {
    const uniqueSpecialityIds = new Set(
      preferences?.map((pref: any) => pref.speciality_id)
    ).size;
    return tatvaShots?.categories?.length === uniqueSpecialityIds;
  };

  const handleSelectAllToggle = () => {
    if (isAllSelected()) {
      setPreferences([]);
    } else {
      const allCategories = tatvaShots?.categories.map((category: any) => ({
        ...category,
        speciality_id: category.id,
      }));
      setPreferences(allCategories);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-white">
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-zinc-200">
        <div className="flex gap-1.5 items-center px-4 py-4">
          <img
            loading="lazy"
            src={backIcon}
            className="object-contain shrink-0 w-6 aspect-square cursor-pointer"
            onClick={() => {
              // Navigate back to the correct tab if returnTab is provided, otherwise use browser history
              if (returnTab) {
                navigate("/short", { state: { type: returnTab } });
              } else {
                navigate(-1);
              }
            }}
          />
          <div className="text-base font-semibold tracking-tight text-zinc-900">
            Preference Settings
          </div>
        </div>
      </div>

      {/* Main Scrollable Content - with padding bottom to account for fixed footer */}
      <div className="flex-1 overflow-y-auto pb-[90px]">
        <div className="p-4">
          <p className="text-[14px] text-left font-normal leading-[22px] text-[#3D4854]">
            You'll see based on your selected specialties and personalize your
            feed anytime.
          </p>

          <div className="flex items-center mb-2">
            <button
              onClick={handleSelectAllToggle}
              className="text-indigo-600 hover:text-indigo-700 text-sm font-medium underline mt-2"
            >
              {isAllSelected() ? "Unselect All" : "Select All"}
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4 mt-4 my-20 text-left">
            {tatvaShots?.categories?.map((category: any) => (
              <div
                key={category.id}
                className={`p-4 border ${!!preferences?.find(
                  (pref: any) => pref?.speciality_id === category?.id
                )
                    ? "border-[#3D8C40] bg-[#EDF7ED]"
                    : "border-[#DCE4E8]"
                  } rounded-2xl flex flex-col gap-x-2 cursor-pointer`}
              >
                <div
                  className="flex justify-between"
                  onClick={() => handleCheckboxChange(category)}
                >
                  <div
                    className={`font-semibold ${preferences?.find(
                      (pref: any) => pref?.speciality_id === category?.id
                    )
                        ? "text-[#3D8C40]"
                        : "text-[#5A6774]"
                      }`}
                  >
                    {category.code}
                  </div>
                  <Checkbox
                    className="category-check-box"
                    checked={
                      !!preferences?.find(
                        (pref: any) => pref?.speciality_id === category?.id
                      )
                    }
                  />
                </div>

                {preferences?.find(
                  (pref: any) => pref?.speciality_id === category?.id
                ) && (
                    <>
                      <div className="mt-2.5 w-full min-h-0 border-[0.5px] border-[#B7DFB9] border-solid" />
                      <div
                        className="flex gap-1 items-center self-center mt-2.5 text-xs text-zinc-700"
                        onClick={() => handlePersonalize(category)}
                      >
                        <img
                          loading="lazy"
                          src={personalizeIcon}
                          className="object-contain shrink-0 w-5 aspect-[1.05]"
                        />
                        <div className="underline">
                          Personalize {category?.code}
                        </div>
                      </div>
                    </>
                  )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="fixed bottom-0 left-0 right-0 w-full bg-white shadow-lg p-4 z-20 max-w-[1280px] mx-auto">
        <Button
          type="primary"
          className="w-full bg-[#4B4AD5] p-[16px] h-[58px] rounded-lg text-[18px] font-medium leading-[26px]"
          onClick={handleSavePreferences}
          loading={loader}
          disabled={loader || !preferences?.length}
        >
          Save changes
        </Button>
      </div>

      <PersonalizeDrawer
        tags={tags}
        selectedTags={selectedTags}
        handleSelectTag={handleSelectTag}
        loader={tagsLoader}
        show={showDrawer}
        handlePersonalizeDrawer={handlePersonalizeDrawer}
        currentCategory={currentCategory}
      />
    </div>
  );
};

export default PreferenceSettings;
